import React from 'react'

import ReactPDF, { Document, Page, StyleSheet, View, usePDF } from '@react-pdf/renderer'
import {
  PerformanceAgreementPrintableAgreementTrainingsFragment,
  PerformanceAgreementQuestionnairesFragment,
  PerformanceProcessPrintableAgreementFragment,
  PerformanceProcessPrintableAgreementTrainingRequestsFragment,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import { Conclusions } from './_Conclusions'
import { AgreementHeader } from './_Header'
import { Questionnaires } from './_Questionnaires'
import { Signatures } from './_Signatures'
import { Trainings } from './_Trainings'

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Inter',
    backgroundColor: '#fff',
    fontSize: 11,
    padding: 40,
  },
  question: {
    flexDirection: 'row',
  },
})

export interface PrintableAgreement {
  agreement: PerformanceProcessPrintableAgreementFragment
  trainingRequests: PerformanceProcessPrintableAgreementTrainingRequestsFragment
  questionnaires: PerformanceAgreementQuestionnairesFragment
  trainings: Array<PerformanceAgreementPrintableAgreementTrainingsFragment>
}

const usePrintableAgreements = (
  agreements?: Array<PrintableAgreement> | null
): { pdf: ReactPDF.UsePDFInstance | null; update: () => void; document: JSX.Element } => {
  const document = <PrintableAgreement agreements={agreements} />

  const [pdf, updatePdf] = usePDF({ document })

  const update = useUnsafeCallback(() => {
    updatePdf(<PrintableAgreement agreements={agreements} />)
  }, [agreements, updatePdf])

  useUnsafeEffect(() => {
    update()
  }, [agreements, update])

  return { pdf: agreements ? pdf : null, update, document }
}

export const usePrintableAgreement = (
  agreement: PerformanceProcessPrintableAgreementFragment | null | undefined,
  questionnaires: PerformanceAgreementQuestionnairesFragment | null | undefined,
  trainingRequests: PerformanceProcessPrintableAgreementTrainingRequestsFragment | null | undefined,
  trainings: Array<PerformanceAgreementPrintableAgreementTrainingsFragment> | null | undefined
) => {
  const agreements = useUnsafeMemo(
    () =>
      agreement && questionnaires && trainingRequests && trainings
        ? [{ agreement, questionnaires, trainingRequests, trainings }]
        : null,
    [agreement, questionnaires, trainingRequests, trainings]
  )

  return usePrintableAgreements(agreements)
}

export const PrintableAgreement: React.FC<{
  agreements?: Array<PrintableAgreement> | null
}> = ({ agreements }) => {
  return (
    <Document>
      {agreements &&
        agreements.map(
          ({ agreement, questionnaires, trainingRequests, trainings }) =>
            agreement.targetSignedAt &&
            agreement.targetSignedAt && (
              <Page size="A4" style={styles.page} key={agreement.id} wrap>
                <View>
                  <AgreementHeader agreement={agreement} />
                  <Questionnaires questionnaires={questionnaires} />
                  <Conclusions agreement={agreement} />
                  <Trainings
                    agreement={agreement}
                    trainingRequests={trainingRequests || []}
                    trainings={trainings}
                  />
                  <Signatures agreement={agreement} />
                </View>
              </Page>
            )
        )}
    </Document>
  )
}
